import React from 'react';
import type { AppProps } from 'next/app';
import Router, { useRouter } from 'next/router';
import { I18nProvider } from 'next-localization';

import { SitecorePageProps } from 'lib/page-props';
import NProgress from 'nprogress';
import Bootstrap from 'src/Bootstrap';

import ModalProvider from '@/components/modals/modal-provider';
import BookingProvider from '@/features/book-appointment/booking-provider';
import { ListingProvider } from '@/features/listing/listing-context';
import { DistanceProvider, QueryProvider } from '@/providers';
import { OasAppProvider } from '@/providers/oas-app-provider';
import { UserDataProvider } from '@/providers/user-data-provider';
import { HeaderProps, HeaderProvider } from '@xsf/shared-lib/components/header';
import { processDictionary } from '@xsf/shared-lib/utils/localization';

import styles from '../assets/common.module.css';

import 'src/temp/theme.css';
// Using bootstrap and nprogress are completely optional.
//  bootstrap is used here to provide a clean layout for samples, without needing extra CSS in the sample app
//  nprogress provides a loading indicator on page/route changes
// Remove these in package.json as well if removed here.
import 'nprogress/nprogress.css';
import '@hcahealthcaredpx/neutron-react/neutron.css';
import '@xsf/shared-lib/assets/shared.css';
import '@/assets/app.css';

NProgress.configure({ showSpinner: false, trickleSpeed: 100 });

Router.events.on('routeChangeStart', () => {
  NProgress.start();
});

Router.events.on('routeChangeComplete', () => {
  NProgress.done();
});
Router.events.on('routeChangeError', () => {
  NProgress.done();
});

function App({ Component, pageProps }: AppProps<SitecorePageProps>): JSX.Element {
  // Replace useLayoutEffect with useEffect during SSR
  if (typeof window === 'undefined') {
    React.useLayoutEffect = React.useEffect;
  }

  const router = useRouter();

  const { dictionary, layoutData, userData, ...rest } = pageProps;
  const processedDictionary = processDictionary(dictionary);
  const header = layoutData?.sitecore?.route?.placeholders?.header?.[0] as unknown as HeaderProps;

  const headerIsSticky = header ? header?.params?.isSticky == '1' : false;

  return (
    <div className={styles.appContainer}>
      <Bootstrap {...pageProps} />
      <I18nProvider lngDict={processedDictionary} locale={pageProps.locale}>
        <QueryProvider>
          <OasAppProvider pageProps={pageProps} query={router.query}>
            <HeaderProvider
              sticky={headerIsSticky}
              headerProps={header ? header : undefined}
              isModule
            >
              <DistanceProvider addressLocation={pageProps.searchAddressData}>
                <ListingProvider>
                  <ModalProvider>
                    <UserDataProvider userData={userData}>
                      <BookingProvider>
                        <Component layoutData={layoutData} {...rest} />
                      </BookingProvider>
                    </UserDataProvider>
                  </ModalProvider>
                </ListingProvider>
              </DistanceProvider>
            </HeaderProvider>
          </OasAppProvider>
        </QueryProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
